/**
 * BundleBuilder-local RHF fork of CommonFields/DesignStepsPage.tsx (shared
 * across bundle types — not editable in place).
 */
import FormSubSection from "../../CommonFields/FormSubSection";
import { FontStyles } from "../../constants";
import { FONTS } from "../../../../utilities/fonts";
import { FormColorPicker, FormInputWithUnit, FormSelect, FormFontsSelect } from "../../../../components/FormFields";
import { useFormContext, useWatch } from "react-hook-form";
import { isDispensaryCoStore } from "../../../constants";

const MobileSubstepLayoutOptions = [
    { label: "Dropdown", value: "dropdown" },
    { label: "Tabs", value: "tabs" },
    { label: "Scrollable Tabs", value: "scrollable_tabs" },
];

const DesignStepsPage = () => {
    const { control } = useFormContext();
    const steps = useWatch({ control, name: "steps" });
    const discountType = useWatch({ control, name: "discount_type" });
    const showProgressBar = useWatch({ control, name: "show_progress_bar" });
    const tierDiscountBasis = useWatch({ control, name: "tier_discount_basis" });
    const isSubStepEnabled = (steps || []).filter((step: any) => step.enable_subSteps)?.length > 0;
    const showProgressBarStyling =
        (!!showProgressBar && discountType === "tier" && tierDiscountBasis !== "product_based") || isDispensaryCoStore;

    return (
        <div>
            <div id="step-general-design">
                <FormSubSection rowLabel="General">
                    <FormColorPicker name="bundles_design.bundle_widget_bg" label="Bundle Widget Background Color" />
                    <FormFontsSelect
                        name="bundles_design.bundle_widget_font_family"
                        label="Font Family"
                        options={FONTS}
                        placeholder="Font style"
                    />
                </FormSubSection>
            </div>

            <div id="step-label-design">
                <FormSubSection rowLabel={"Step Label"}>
                    <FormColorPicker name="bundles_design.step_label_color" label="Text Color" />
                    <FormColorPicker name="bundles_design.step_circle_bg_color" label="Completed BG Color" />
                    <FormColorPicker name="bundles_design.step_circle_color" label="Default Color" />
                </FormSubSection>
            </div>

            <div id="step-fixed-boxes-design">
                <FormSubSection rowLabel="Fixed Boxes">
                    <FormColorPicker name="bundles_design.fixed_boxes_bg_color" label="Box Color" />
                    <FormColorPicker name="bundles_design.fixed_boxes_selected_bg_color" label="Selected Box Color" />
                    <FormColorPicker name="bundles_design.fixed_boxes_text_color" label="Text Color" />
                    <FormColorPicker
                        name="bundles_design.fixed_boxes_selected_text_color"
                        label="Selected Text Color"
                    />
                    <FormInputWithUnit
                        name="bundles_design.fixed_boxes_text_size"
                        min={0}
                        label="Text Size"
                        placeholder="text size"
                        unitSuffix="px"
                    />
                </FormSubSection>
            </div>

            {isSubStepEnabled && (
                <div id="step-cta-design">
                    <FormSubSection rowLabel="Substep CTA">
                        <FormColorPicker name="bundles_design.substep_bg_color" label="Background Color" />
                        <FormColorPicker name="bundles_design.substep_text_color" label="Text Color" />
                        <FormInputWithUnit
                            name="bundles_design.substep_header_text_size"
                            min={0}
                            label="Size"
                            placeholder="font size"
                            unitSuffix="px"
                        />
                        <FormSelect
                            name="bundles_design.mobile_substep_header_layout"
                            label="Mobile Layout"
                            options={MobileSubstepLayoutOptions}
                        />
                    </FormSubSection>
                </div>
            )}

            <div id="step-page-title">
                <FormSubSection rowLabel="Page Title">
                    <FormColorPicker name="bundles_design.step_page_title_color" label="Text Color" />
                    <FormInputWithUnit
                        name="bundles_design.step_page_title_size"
                        min={0}
                        label="Size"
                        placeholder="font size"
                        unitSuffix="px"
                    />
                    <FormSelect
                        name="bundles_design.step_page_title_style"
                        label="Style"
                        options={FontStyles}
                        placeholder="Font style"
                    />
                </FormSubSection>
            </div>

            <div id="step-page-subtitle">
                <FormSubSection rowLabel="Page Subtitle">
                    <FormColorPicker name="bundles_design.step_page_subtitle_color" label="Text Color" />
                    <FormInputWithUnit
                        name="bundles_design.step_page_subtitle_size"
                        min={0}
                        label="Size"
                        placeholder="font size"
                        unitSuffix="px"
                    />
                    <FormSelect
                        name="bundles_design.step_page_subtitle_style"
                        label="Style"
                        options={FontStyles}
                        placeholder="Font style"
                    />
                </FormSubSection>
            </div>

            <div id="step-bottom-bar">
                <FormSubSection rowLabel="Bottom Bar">
                    <FormColorPicker name="bundles_design.total_footer_background_color" label="Background Color" />
                    <FormColorPicker name="bundles_design.total_footer_text_color" label="Text Color" />
                    <FormInputWithUnit
                        name="bundles_design.total_footer_text_size"
                        min={0}
                        label="Size"
                        placeholder="font size"
                        unitSuffix="px"
                    />
                    <FormSelect
                        name="bundles_design.total_footer_text_style"
                        label="Style"
                        options={FontStyles}
                        placeholder="Font style"
                    />
                    <FormColorPicker name="bundles_design.total_footer_price_color" label="Price Color" />
                    <FormColorPicker
                        name="bundles_design.total_footer_strike_off_price_color"
                        label="Strike off Price Color"
                    />
                    <FormColorPicker name="bundles_design.total_footer_discount_color" label="Discount Color" />
                    {showProgressBarStyling && (
                        <>
                            <FormColorPicker name="bundles_design.progress_bar_color" label="Progress Bar Foreground" />
                            <FormColorPicker
                                name="bundles_design.progress_bar_bg_color"
                                label="Progress Bar Background"
                            />
                        </>
                    )}
                </FormSubSection>
            </div>

            <div id="step-next-button">
                <FormSubSection rowLabel="Next Button">
                    <FormColorPicker name="bundles_design.secondary_cta_bg_color" label="Background Color" />
                    <FormColorPicker name="bundles_design.secondary_cta_text_color" label="Text Color" />
                </FormSubSection>
            </div>

            <div id="step-previous-button">
                <FormSubSection rowLabel="Previous Button" hideSeparator>
                    <FormColorPicker name="bundles_design.tertiary_cta_bg_color" label="Background Color" />
                    <FormColorPicker name="bundles_design.tertiary_cta_text_color" label="Text Color" />
                </FormSubSection>
            </div>
        </div>
    );
};

export default DesignStepsPage;
